import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from '@/api/types/tenants';

// Mirrors the "Change Admin Password" modal in Index.cshtml / index.js:
// current admin password + new password (min 6), with eye toggles.

const schema = z.object({
  oldPassword: z.string().min(1, 'Enter the current admin password.').max(MAX_PASSWORD_LENGTH),
  newPassword: z
    .string()
    .min(MIN_PASSWORD_LENGTH, `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
    .max(MAX_PASSWORD_LENGTH),
});

export type ResetPasswordFormValues = z.infer<typeof schema>;

interface ResetPasswordModalProps {
  show: boolean;
  tenantName: string;
  busy?: boolean;
  onSubmit: (values: ResetPasswordFormValues) => void;
  onClose: () => void;
}

export function ResetPasswordModal({
  show,
  tenantName,
  busy = false,
  onSubmit,
  onClose,
}: ResetPasswordModalProps) {
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { oldPassword: '', newPassword: '' },
  });

  return (
    <Modal
      show={show}
      title={`Change Admin Password — ${tenantName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="reset-admin-password-form"
            className="btn btn-primary"
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Set password'}
          </button>
        </>
      }
    >
      <form id="reset-admin-password-form" onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
        <div className="mb-3">
          <label className="form-label" htmlFor="resetOldPassword">
            Current admin password
          </label>
          <div className="input-group">
            <input
              id="resetOldPassword"
              type={showOld ? 'text' : 'password'}
              className={`form-control${errors.oldPassword ? ' is-invalid' : ''}`}
              autoComplete="current-password"
              maxLength={MAX_PASSWORD_LENGTH}
              {...register('oldPassword')}
            />
            <button
              className="btn btn-light"
              type="button"
              onClick={() => setShowOld((v) => !v)}
              aria-label={showOld ? 'Hide password' : 'Show password'}
            >
              <i className={`fa ${showOld ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
            </button>
          </div>
          {errors.oldPassword && (
            <div className="text-danger small mt-1">{errors.oldPassword.message}</div>
          )}
        </div>

        <div className="mb-1">
          <label className="form-label" htmlFor="resetNewPassword">
            New password
          </label>
          <div className="input-group">
            <input
              id="resetNewPassword"
              type={showNew ? 'text' : 'password'}
              className={`form-control${errors.newPassword ? ' is-invalid' : ''}`}
              autoComplete="new-password"
              maxLength={MAX_PASSWORD_LENGTH}
              {...register('newPassword')}
            />
            <button
              className="btn btn-light"
              type="button"
              onClick={() => setShowNew((v) => !v)}
              aria-label={showNew ? 'Hide password' : 'Show password'}
            >
              <i className={`fa ${showNew ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
            </button>
          </div>
          {errors.newPassword && (
            <div className="text-danger small mt-1">{errors.newPassword.message}</div>
          )}
        </div>
      </form>
    </Modal>
  );
}
